import { defineStore } from "pinia";

import { EventsState, Hit, Filters } from "../interfaces/knowledge-hub";
import { Filter, Facet, FacetCount, Result } from "../interfaces/facets";

import { useSearchParameters, useTypesenseClient } from "../composables/typesense";

export const useEventsStore = defineStore("events", {
	state: (): EventsState => ({
		loading: false,
		facets: [],
		filters: {
			searchQuery: "",
			filterQuery: "",
			date: {
				field: "postDateYear",
				values: [],
				combination: "&&",
			},
			pillars: {
				field: "pillars",
				values: [],
				combination: "&&",
			},
			industries: {
				field: "industries",
				values: [],
				combination: "&&",
			},
			solutions: {
				field: "solutions",
				values: [],
				combination: "&&",
			},
			type: {
				field: "type",
				values: [],
				combination: "&&",
			},
		},
		pagination: {
			current: 1,
			totalPages: 0,
			totalResults: 0,
			foundResults: 0,
			itemsPerPage: 9,
		},
		sorting: {
			type: "post_date_timestamp",
			order: "desc",
		},
		index: import.meta.env.VITE_EVENTS_INDEX ?? "wdp_production_events",
		events: null,
	}),
	actions: {
		async fetch(reset = true) {
			this.loading = true;
			this.setFiltersQuery();

			const client = useTypesenseClient();
			const searchParameters = useSearchParameters(this.filters, this.sorting, "title");

			if (reset) {
				this.pagination.current = 1;
				this.events = [];
			} else {
				this.pagination.current++;
			}

			const searchRequests = {
				searches: [
					{
						collection: this.index,
						facet_by: "postDateYear",
						max_facet_values: 100,
						page: this.pagination.current,
						per_page: this.pagination.itemsPerPage,
						...searchParameters,
					},
				],
			};

			await client.multiSearch.perform(searchRequests).then((results: any) => {
				if (results.results) {
					results.results.forEach((result: Result) => {
						if (result.hits && this.events) {
							result.hits.forEach((hit: Hit) => {
								if (this.events) this.events.push(hit);
							});
						}

						this.pagination.foundResults = result.found;
						this.pagination.totalResults = result.out_of;
						if (result.facet_counts) {
							this.facets = result.facet_counts;
						}
					});

					this.pagination.totalPages = Math.ceil(
						this.pagination.foundResults / (this.pagination.itemsPerPage ?? 9),
					);
				}
				this.loading = false;
			});
		},
		setFilter(type: keyof Filters, value: string) {
			const settedFilter: Filter | string = this.filters[type];

			if (typeof settedFilter !== "string") {
				if (settedFilter.values.indexOf(value) < 0) {
					settedFilter.values.push(value);
				} else {
					settedFilter.values = settedFilter.values.filter((item: string) => item !== value);
				}
			}

			this.setFiltersQuery();
			this.fetch(true);
		},
		setFiltersQuery() {
			const filterParts: string[] = [];
			const language = window.language;

			filterParts.push(`site:${language}`);
			filterParts.push(`hideInFrontEndSearch:=false`);

			if (this.filters.date.values.length > 0) {
				filterParts.push(`${this.filters.date.field}:=[${this.filters.date.values.join(",")}]`);
			}

			this.filters.filterQuery = filterParts.join(" && ");
		},
	},
});
